import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { clearGameCache } from '@/api/games'
import type { DismissReason, Friend, RecommendationEntry } from '@/types'
import { GameCover } from '@/components/ui/GameCover'
import { CoopModesBadges, CoopPlayersBadge } from '@/components/ui/CoopBadges'
import { HltbBadge } from '@/components/ui/HltbBadge'
import { PriceTag } from '@/components/ui/PriceTag'
import { RatingPill } from '@/components/ui/RatingPill'
import { Icon } from '@/components/ui/Icon'
import { Avatar } from '@/components/ui/Avatar'
import { DismissButton } from './DismissButton'

export interface WishlistMeta {
  wishers: Friend[]
  currentUserId?: string
}

interface RecommendationCardProps {
  entry: RecommendationEntry
  groupSize: number
  onAddToWishlist: () => void
  onAddToPlaying: () => void
  onAddToPlayed: () => void
  onViewDetails: () => void
  onDismiss: (reason: DismissReason | null) => void
  isAddingToWishlist: boolean
  isAddingToPlaying: boolean
  isAddingToPlayed: boolean
  isDismissing: boolean
  wishlistMeta?: WishlistMeta
  canEdit?: boolean
}

export function RecommendationCard({
  entry,
  groupSize,
  onAddToWishlist,
  onAddToPlaying,
  onAddToPlayed,
  onViewDetails,
  onDismiss,
  isAddingToWishlist,
  isAddingToPlaying,
  isAddingToPlayed,
  isDismissing,
  wishlistMeta,
  canEdit = true,
}: RecommendationCardProps) {
  const { game, reason, ownedBy } = entry
  const isWishlist = wishlistMeta !== undefined
  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()

  const clearCacheMutation = useMutation({
    mutationFn: () => clearGameCache(game.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['recommendations'] }),
  })

  return (
    <div className={isWishlist ? 'cg-rec-card intersect' : 'cg-rec-card'}>
      {wishlistMeta && (
        <div className="cg-rec-intersect-ribbon">
          <div className="cg-rec-intersect-meta">
            <span className="cg-rec-intersect-count">{wishlistMeta.wishers.length}</span>
            <span className="cg-rec-intersect-label">
              on personal wishlist{wishlistMeta.wishers.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="cg-owner-stack">
            {wishlistMeta.wishers.map((f) => (
              <Avatar
                key={f.id}
                friend={f}
                size={22}
                showRing={f.id === wishlistMeta.currentUserId}
              />
            ))}
          </div>
        </div>
      )}
      <div className="cg-rec-cover-wrapper" style={{ position: 'relative' }}>
        <GameCover game={game} size="md" />
        <button
          className="cg-btn ghost tiny"
          onClick={onViewDetails}
          style={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-1)',
            opacity: 0.9,
            whiteSpace: 'nowrap',
          }}
        >
          <Icon name="eye" size={11} /> View details
        </button>
      </div>

      <div className="cg-rec-body">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg-0)', lineHeight: 1.3 }}>
            {game.title}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <a
              href={`https://store.steampowered.com/app/${game.appId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cg-icon-btn"
              title="View on Steam"
              style={{ flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img
                src="https://store.steampowered.com/favicon.ico"
                alt="Steam"
                style={{ width: 14, height: 14, filter: 'grayscale(0.2) contrast(1.1)' }}
              />
            </a>
            {canEdit && (
              <DismissButton
                onDismiss={onDismiss}
                disabled={isDismissing}
                inline
                title="Not interested"
              />
            )}
          </div>
        </div>

        <p style={{ fontSize: 12.5, color: 'var(--fg-2)', fontStyle: 'italic', margin: 0, lineHeight: 1.5 }}>
          {reason}
        </p>

        <div className="cg-coop compact">
          <CoopPlayersBadge game={game} groupSize={groupSize} />
          <HltbBadge game={game} />
          <CoopModesBadges coop={game.coop} compact />
        </div>

        {ownedBy.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>
            Already owned by {ownedBy.length === 1 ? 'a member' : `${ownedBy.length} members`}
          </div>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="cg-rec-meta">
            {game.rating > 0 && game.reviews > 0 && (
              <RatingPill rating={game.rating} reviews={game.reviews} />
            )}
            <PriceTag game={game} compact />
          </div>

          {canEdit && (
            <>
              <button
                className="cg-btn cg-btn-primary"
                data-action="wishlist"
                onClick={onAddToWishlist}
                disabled={isAddingToWishlist || isAddingToPlaying || isAddingToPlayed}
                style={{ width: '100%' }}
              >
                <Icon name="wishlist" size={12} />
                {isAddingToWishlist ? 'Adding…' : 'Wishlist'}
              </button>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="cg-btn"
                  data-action="playing"
                  onClick={onAddToPlaying}
                  disabled={isAddingToWishlist || isAddingToPlaying || isAddingToPlayed}
                  style={{ flex: 1, fontSize: 11.5, padding: '5px 8px', gap: 4, justifyContent: 'center' }}
                >
                  <Icon name="controller" size={11} />
                  {isAddingToPlaying ? 'Starting…' : 'Start playing'}
                </button>
                <button
                  className="cg-btn"
                  data-action="played"
                  onClick={onAddToPlayed}
                  disabled={isAddingToWishlist || isAddingToPlaying || isAddingToPlayed}
                  style={{ flex: 1, fontSize: 11.5, padding: '5px 8px', gap: 4, justifyContent: 'center' }}
                >
                  <Icon name="star" size={11} />
                  {isAddingToPlayed ? 'Marking…' : 'Mark played'}
                </button>
              </div>
            </>
          )}
          {isAdmin && (
            <button
              className="cg-btn ghost tiny"
              onClick={() => clearCacheMutation.mutate()}
              disabled={clearCacheMutation.isPending}
            >
              {clearCacheMutation.isPending ? 'Clearing…' : 'Clear cache'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
